import { ChargeItemDefinitionCode, Currency } from '@quromedical/fhir-common';
import { z } from 'zod';
import { UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema, PeriodSchema } from '../domain/schema';

export const chargeItemDefinitionStatus = [
  'draft',
  'active',
  'retired',
  'unknown',
] as const;

export const chargeItemDefinitionChargeLineType = [
  'base',
  'surcharge',
  'deduction',
  'discount',
  'tax',
  'informational',
] as const;

/**
 * Stored as part of the `applicability` field. In FHIR each item here will represent a different
 * applicability condition
 */
export const ChargeLineApplicabilitySchema = z.object({
  administratorCode: z.string().optional(),
  schemeCode: z.string().optional(),
  description: z.string().optional(),
});

export enum ChargeItemDefinitionMoneyFactor {
  cents = 0.01,
}

export const MONEY_FACTOR = ChargeItemDefinitionMoneyFactor.cents;

export const ChargeItemDefinitionMoneyFactorSchema = z.literal(MONEY_FACTOR);

export const ChargeItemDefinitionCurrencySchema = z.nativeEnum(Currency);

export const ChargeItemDefinitionCodeSchema = z.nativeEnum(
  ChargeItemDefinitionCode,
);

export const ChargeItemDefinitionStatusSchema = z.enum(
  chargeItemDefinitionStatus,
);

export const ChargeItemDefinitionTypeSchema = z.enum(
  chargeItemDefinitionChargeLineType,
);

/**
 * Store money as cents to prevent any decimal math issues
 */
export const ChargeItemDefinitionMoneySchema = z.object({
  /**
   * Always 0.01 since we're working with cents (100 cents = 1 currency) since we always store this
   * as cents
   */
  factor: ChargeItemDefinitionMoneyFactorSchema,
  currency: ChargeItemDefinitionCurrencySchema,
  value: z.number(),
});

export const ChargeLineSchema = z.object({
  type: ChargeItemDefinitionTypeSchema,
  applicability: ChargeLineApplicabilitySchema,
  code: z.string(),
  amount: ChargeItemDefinitionMoneySchema,
});

export const ChargeItemDefinitionSchema = z.object({
  /**
   * Code that represents the type of the charge item
   */
  code: ChargeItemDefinitionCodeSchema,
  title: z.string(),
  description: z.string(),
  status: ChargeItemDefinitionStatusSchema,

  /**
   * Date last changed
   */
  date: z.string(),
  /**
   * Charge/Claim lines as they apply to different medical aids/etc.
   */
  chargeLines: z.array(ChargeLineSchema).min(1),

  effectivePeriod: PeriodSchema,
});

export const ChargeItemDefinitionCreateSchema = ChargeItemDefinitionSchema.omit(
  {
    status: true,
  },
);

export const ChargeItemDefinitionSchemaPartial = z
  .object({
    code: ChargeItemDefinitionCodeSchema,
    title: z.string(),
    description: z.string(),
    status: ChargeItemDefinitionStatusSchema,
    date: z.string(),
    effectivePeriod: PeriodSchema,
  })
  .partial()
  .extend({
    chargeLines: ChargeLineSchema.partial().array(),
  });

export const ChargeItemDefinitionSchemaWithId =
  ChargeItemDefinitionSchema.extend({
    id: UUIDSchema,
  });

export const ChargeItemDefinitionGetResponseSchema =
  ChargeItemDefinitionSchemaPartial.extend({
    id: UUIDSchema,
  });

export const ChargeItemDefinitionGetAllResponseSchema =
  PaginatedBundleSchema.extend({
    chargeItemDefinitions: ChargeItemDefinitionGetResponseSchema.array(),
  });

export const ChargeItemDefinitionGetAllParamsSchema = PaginationSchema.extend({
  ids: UUIDSchema.optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  status: ChargeItemDefinitionStatusSchema.optional(),
  effectivePeriodStart: z.string().optional(),
  effectivePeriodEnd: z.string().optional(),
});

/**
 * A `ChargeItem.propertyGroup.priceComponent`
 */
export type ChargeLine = z.infer<typeof ChargeLineSchema>;

export type ChargeLineApplicability = z.infer<
  typeof ChargeLineApplicabilitySchema
>;

export type ChargeItemDefinitionMoney = z.infer<
  typeof ChargeItemDefinitionMoneySchema
>;

export type ChargeItemDefinition = z.infer<typeof ChargeItemDefinitionSchema>;

export type PartialChargeItemDefinition = z.infer<
  typeof ChargeItemDefinitionSchemaPartial
>;

export type ChargeItemDefinitionGetResponse = z.infer<
  typeof ChargeItemDefinitionGetResponseSchema
>;

export type ChargeItemDefinitionGetAllResponse = z.infer<
  typeof ChargeItemDefinitionGetAllResponseSchema
>;

export type CreateChargeItemDefinition = z.infer<
  typeof ChargeItemDefinitionCreateSchema
>;
export type CreateChargeItemDefinitionResponse = z.infer<
  typeof ChargeItemDefinitionGetResponseSchema
>;

export type UpdateChargeItemDefinition = z.infer<
  typeof ChargeItemDefinitionSchemaWithId
>;
export type UpdateChargeItemDefinitionResponse =
  ChargeItemDefinitionGetResponse;

export type ChargeItemDefinitionGetAllParams = z.infer<
  typeof ChargeItemDefinitionGetAllParamsSchema
>;
